import { Clear } from "@mui/icons-material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import {
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
} from "@mui/material";
import { isUndefined } from "lodash";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../hooks/useApi";
import { Profile } from "../types/contact";
import { Filters } from "../types/filters";

interface Props {
  type: "outstanding" | "recently approved" | "history";
  onApplyFilters: (filters: {
    candidate: string;
    bookingContact: string;
    jobDescription: string;
    jobTitle: string;
    location: string;
    client: string;
    weekEnding: string;
  }) => void;
  isEnabled: boolean;
  defaultFilters?: Filters; // used to pass the filters when the already exist, e.g.
}

const TimesheetFilters: FunctionComponent<Props> = (
  props: Props
): JSX.Element => {
  const { onApplyFilters, type, isEnabled, defaultFilters } = props;
  const [weekEnding, setWeekEnding] = useState<string>("");
  const [candidate, setCandidate] = useState<string>("");
  const [bookingContact, setBookingContact] = useState<string>("");
  const [jobTitle, setJobTitle] = useState<string>("");
  const [jobDescription, setJobDescription] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [client, setClient] = useState<string>("");
  const [isApplyClicked, setIsApplyClicked] = useState<boolean>(false);
  const [appliedFilter, setAppliedFilter] = useState<
    | {
        candidate: string;
        bookingContact: string;
        jobDescription: string;
        jobTitle: string;
        client: string;
        location: string;
        weekEnding: string;
      }
    | undefined
  >(undefined);

  const { data: profile } = useApi<Profile>("profile");
  const { data: loadedFilters, isLoading } = useApi<Filters>(
    "filters",
    {
      status:
        type === "outstanding"
          ? [0, 2, 3]
          : type === "recently approved"
          ? [1]
          : [4],
    },
    undefined,
    isEnabled && isUndefined(defaultFilters)
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filters, setFilters] = useState<Filters>();

  useEffect(() => {
    if (loadedFilters && loadedFilters.results) {
      setFilters(loadedFilters.results);
    } else if (defaultFilters) {
      setFilters(defaultFilters);
    }
  }, [defaultFilters, loadedFilters]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const { t } = useTranslation();

  return (
    <>
      <>
        <Stack
          direction={"row"}
          alignItems={"center"}
          flexWrap={"wrap"}
          spacing={1}
          sx={{ pb: "20px" }}
        >
          <Button
            variant="text"
            startIcon={<FilterAltIcon />}
            onClick={handleClick}
            disabled={isLoading || !isEnabled}
          >
            {t("tsTable.filters")}
          </Button>

          {isApplyClicked && appliedFilter?.weekEnding !== "" && (
            <Chip
              sx={{ backgroundColor: "rgba(0, 82, 146, 0.1)" }}
              label={`${t("tsTable.weekEnding")}: "${weekEnding}"`}
              size="small"
            />
          )}

          {isApplyClicked && appliedFilter?.candidate !== "" && (
            <Chip
              sx={{ backgroundColor: "rgba(0, 82, 146, 0.1)" }}
              label={`${t("tsTable.candidate")}: "${candidate}"`}
              size="small"
            />
          )}

          {isApplyClicked && appliedFilter?.bookingContact !== "" && (
            <Chip
              sx={{ backgroundColor: "rgba(0, 82, 146, 0.1)" }}
              label={`${t("tsTable.bookingContact")}: "${bookingContact}"`}
              size="small"
            />
          )}

          {isApplyClicked && appliedFilter?.jobTitle !== "" && (
            <Chip
              sx={{ backgroundColor: "rgba(0, 82, 146, 0.1)" }}
              label={`${t("tsTable.jobTitle")}: "${jobTitle}"`}
              size="small"
            />
          )}

          {isApplyClicked && appliedFilter?.jobDescription !== "" && (
            <Chip
              sx={{ backgroundColor: "rgba(0, 82, 146, 0.1)" }}
              label={`${t("tsTable.jobDescription")}: "${jobDescription}"`}
              size="small"
            />
          )}

          {isApplyClicked && appliedFilter?.location !== "" && (
            <Chip
              sx={{ backgroundColor: "rgba(0, 82, 146, 0.1)" }}
              label={`${t("tsTable.location")}: "${location}"`}
              size="small"
            />
          )}

          {isApplyClicked && appliedFilter?.client !== "" && (
            <Chip
              sx={{ backgroundColor: "rgba(0, 82, 146, 0.1)" }}
              label={`${t("tsTable.clientName")}: "${client}"`}
              size="small"
            />
          )}

          {isApplyClicked && (
            <IconButton
              size="small"
              // variant="text"
              sx={{ color: "#005292" }}
              onClick={() => {
                setLocation("");
                setCandidate("");
                setBookingContact("");
                setClient("");
                setJobDescription("");
                setJobTitle("");
                setWeekEnding("");
                setAppliedFilter(undefined);
                onApplyFilters({
                  candidate: "",
                  bookingContact: "",
                  jobDescription: "",
                  jobTitle: "",
                  client: "",
                  location: "",
                  weekEnding: "",
                });
                setIsApplyClicked(false);
                setAnchorEl(null);
              }}
            >
              <Clear />
            </IconButton>
          )}
        </Stack>

        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Stack
            direction={"column"}
            spacing={2}
            padding={3}
            style={{ width: 300 }}
          >
            <FormControl>
              <InputLabel size="small">{t("tsTable.weekEnding")}</InputLabel>
              <Select
                value={weekEnding}
                label={t("tsTable.weekEnding")}
                onChange={(event) => setWeekEnding(event.target.value)}
                size="small"
              >
                {filters?.weekEndings.map((f, index) => (
                  <MenuItem key={index} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel size="small">{t("tsTable.candidate")}</InputLabel>
              <Select
                value={candidate}
                label={t("tsTable.candidate")}
                onChange={(event) => setCandidate(event.target.value)}
                size="small"
              >
                {filters?.candidates.map((f, index) => (
                  <MenuItem key={index} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel size="small">
                {t("tsTable.bookingContact")}
              </InputLabel>
              <Select
                value={bookingContact}
                label={t("tsTable.bookingContact")}
                onChange={(event) => setBookingContact(event.target.value)}
                size="small"
              >
                {filters?.bookingContacts.map((f, index) => (
                  <MenuItem key={index} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel size="small">{t("tsTable.jobTitle")}</InputLabel>
              <Select
                value={jobTitle}
                label={t("tsTable.jobTitle")}
                onChange={(event) => setJobTitle(event.target.value)}
                size="small"
              >
                {filters?.jobTitles.map((f, index) => (
                  <MenuItem key={index} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel size="small">
                {t("tsTable.jobDescription")}
              </InputLabel>
              <Select
                value={jobDescription}
                label={t("tsTable.jobDescription")}
                onChange={(event) => setJobDescription(event.target.value)}
                size="small"
              >
                {filters?.jobDescriptions.map((f, index) => (
                  <MenuItem key={index} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel size="small">{t("tsTable.location")}</InputLabel>
              <Select
                value={location}
                label={t("tsTable.location")}
                onChange={(event) => setLocation(event.target.value)}
                size="small"
              >
                {filters?.locations.map((f, index) => (
                  <MenuItem key={index} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {profile?.results?.clients &&
              profile?.results?.clients.length > 1 && (
                <FormControl>
                  <InputLabel size="small">
                    {t("tsTable.clientName")}
                  </InputLabel>
                  <Select
                    value={client}
                    label={t("tsTable.clientName")}
                    onChange={(event) => setClient(event.target.value)}
                    size="small"
                  >
                    {filters?.clients.map((f, index) => (
                      <MenuItem key={index} value={f}>
                        {f}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

            <Stack spacing={2} sx={{ pt: "20px" }}>
              <Button
                variant="contained"
                onClick={() => {
                  setAppliedFilter({
                    candidate,
                    bookingContact,
                    jobDescription,
                    jobTitle,
                    client,
                    location,
                    weekEnding,
                  });
                  onApplyFilters({
                    candidate,
                    bookingContact,
                    jobDescription,
                    jobTitle,
                    client,
                    location,
                    weekEnding,
                  });
                  setIsApplyClicked(true);
                  setAnchorEl(null);
                }}
              >
                {t("tsTable.apply")}
              </Button>

              <Button
                variant="text"
                onClick={() => {
                  setLocation("");
                  setCandidate("");
                  setBookingContact("");
                  setClient("");
                  setJobDescription("");
                  setJobTitle("");
                  setWeekEnding("");
                  onApplyFilters({
                    candidate: "",
                    bookingContact: "",
                    jobDescription: "",
                    jobTitle: "",
                    client: "",
                    location: "",
                    weekEnding: "",
                  });
                  setIsApplyClicked(false);
                  setAnchorEl(null);
                }}
              >
                {t("tsTable.clear")}
              </Button>
            </Stack>
          </Stack>
        </Popover>
      </>
    </>
  );
};

export default TimesheetFilters;
